import React, { useState, useEffect, useRef } from 'react';
import { isKatakana } from '@this/src/util';
import type {
  ReservationInfo,
  TravelerFields,
  TravelerFormValues,
  DepartmentOption
} from '../../api/reservationInfo';
import ReservationInfoCommonSection from './ReservationInfoCommonSection';
import ReservationInfoShinkansenSection from './ReservationInfoShinkansenSection';
import ReservationInfoAirSection from './ReservationInfoAirSection';
import {
  SHINKANSEN_ADDRESS_TYPES,
  type CommonErrors,
  type CommonFormValues,
  type ShinkansenFormValues,
  type FormValues
} from './ReservationInfoFormTypes';

type Props = {
  reservationInfo: ReservationInfo;
  onSubmit: (values: FormValues) => void;
  isSubmitting?: boolean;
  editable?: boolean;
};

const buildInitialTravelerValues = (travelerFields: TravelerFields | null): TravelerFormValues | null => {
  if (travelerFields == null) return null;
  const { currentValues } = travelerFields;
  return {
    flightFirstName: currentValues.flightFirstName || '',
    flightLastName: currentValues.flightLastName || '',
    flightFirstNameKana: currentValues.flightFirstNameKana || '',
    flightLastNameKana: currentValues.flightLastNameKana || '',
    flightFirstNameRoman: currentValues.flightFirstNameRoman || '',
    flightLastNameRoman: currentValues.flightLastNameRoman || '',
    flightBirthday: currentValues.flightBirthday || '',
    flightGender: currentValues.flightGender || '',
    flightTel: currentValues.flightTel || '',
    airSeat: currentValues.airSeat || ''
  };
};

const parseShinkansenSeat = (
  seat: string | null
): { seat: ShinkansenFormValues['shinkansenSeat']; detail: string } => {
  if (seat == null || seat === '') return { seat: '指定なし', detail: '' };
  const [base, detail] = seat.split(':');
  return { seat: (base as ShinkansenFormValues['shinkansenSeat']) || '指定なし', detail: detail || '' };
};

const buildInitialShinkansenValues = (
  shinkansenInfo: ReservationInfo['shinkansenInfo'],
  departments: DepartmentOption[]
): ShinkansenFormValues | null => {
  if (shinkansenInfo == null) return null;
  const { currentValues, settings } = shinkansenInfo;
  const seat = parseShinkansenSeat(currentValues.shinkansenSeat);
  const addressType =
    currentValues.shinkansenAddressType ||
    (settings.enabledCustomizeAddress
      ? SHINKANSEN_ADDRESS_TYPES.text
      : settings.hasDepartments
      ? SHINKANSEN_ADDRESS_TYPES.select
      : SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect);
  const defaultDepartmentId = settings.hasDepartments ? departments[0]?.id || null : null;
  const defaultBaseId = null;
  const selectedDepartment = departments.find(dept => dept.id === defaultDepartmentId);
  const addressFromDepartment = selectedDepartment?.address || '';
  const addresseeFromDepartment = selectedDepartment?.addressee || '';
  const postcodeFromDepartment = selectedDepartment?.postalCode || '';
  const addressFromBase = '';
  const addresseeFromBase = '';
  const postcodeFromBase = '';
  const organizationAddressId = selectedDepartment?.organizationAddressId || null;

  return {
    shinkansenAddressType: addressType,
    postcode:
      addressType === SHINKANSEN_ADDRESS_TYPES.select
        ? currentValues.postcode || postcodeFromDepartment
        : addressType === SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect
        ? postcodeFromBase
        : currentValues.postcode || '',
    shinkansenAddress:
      addressType === SHINKANSEN_ADDRESS_TYPES.select
        ? currentValues.shinkansenAddress || addressFromDepartment
        : addressType === SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect
        ? addressFromBase
        : currentValues.shinkansenAddress || '',
    shinkansenAddressee:
      addressType === SHINKANSEN_ADDRESS_TYPES.select
        ? currentValues.shinkansenAddressee || addresseeFromDepartment
        : addressType === SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect
        ? addresseeFromBase
        : currentValues.shinkansenAddressee || '',
    shinkansenTicketType: currentValues.shinkansenTicketType || 'normal',
    shinkansenSeatType: currentValues.shinkansenSeatType || 'reserve',
    shinkansenSeat: seat.seat,
    shinkansenSeatDetail: seat.detail,
    exicNumber: currentValues.exicNumber || '',
    exicPassword: '',
    exicCardId: currentValues.exicCardId || null,
    departmentId: defaultDepartmentId,
    organizationBaseId: defaultBaseId,
    organizationAddressId: addressType === SHINKANSEN_ADDRESS_TYPES.select ? organizationAddressId : null,
    preferredTicketingMethod: defaultPreferredTicketingMethod(currentValues.preferredTicketingMethod, settings)
  };
};

const defaultPreferredTicketingMethod = (
  current: 'qr' | 'postal' | null,
  settings: NonNullable<ReservationInfo['shinkansenInfo']>['settings']
): 'qr' | 'postal' | '' => {
  if (current) return current;
  if (settings.usePostal && !settings.useQr) return 'postal';
  if (settings.useQr) return 'qr';
  return '';
};

const ReservationInfoForm = ({ reservationInfo, onSubmit, isSubmitting = false, editable = true }: Props) => {
  const {
    currentValues,
    settingFlags,
    options,
    travelerFields,
    hotelFields,
    shinkansenInfo,
    transportTypes,
    approvalRequired
  } = reservationInfo;

  const [formValues, setFormValues] = useState<FormValues>({
    finalDestination: currentValues.finalDestination || '',
    purpose: currentValues.purpose || '',
    internalNumber: currentValues.internalNumber || '',
    chargingDepartmentId: currentValues.chargingDepartmentId,
    projectId: currentValues.projectId,
    traveler: buildInitialTravelerValues(travelerFields),
    shinkansen: buildInitialShinkansenValues(shinkansenInfo, options.departments)
  });

  const [hotelFormValues, setHotelFormValues] = useState<TravelerFormValues | null>(
    buildInitialTravelerValues(hotelFields)
  );

  // reservationInfo props は親 (TripAiMessagesPage) で Pusher 受信ごとに refetch されるため
  // 参照が頻繁に変わる。シリアライズ値を比較し、実際に内容が変わった場合のみ初期化し直す。
  // (内容が同じなのに再初期化するとユーザーの編集中の値がリセットされる)
  const lastInitKeyRef = useRef<string>('');
  useEffect(() => {
    const initKey = JSON.stringify({
      travelerFields,
      hotelFields,
      shinkansenInfo,
      departments: options.departments
    });
    if (lastInitKeyRef.current === initKey) return;
    lastInitKeyRef.current = initKey;
    setFormValues(prev => ({
      ...prev,
      traveler: buildInitialTravelerValues(travelerFields),
      shinkansen: buildInitialShinkansenValues(shinkansenInfo, options.departments)
    }));
    setHotelFormValues(buildInitialTravelerValues(hotelFields));
  }, [travelerFields, hotelFields, shinkansenInfo, options.departments]);

  useEffect(() => {
    setFormValues(prev => ({
      ...prev,
      finalDestination: currentValues.finalDestination || '',
      purpose: currentValues.purpose || '',
      internalNumber: currentValues.internalNumber || '',
      chargingDepartmentId: currentValues.chargingDepartmentId,
      projectId: currentValues.projectId
    }));
  }, [currentValues]);

  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});
  const [travelerErrors, setTravelerErrors] = useState<Partial<Record<keyof TravelerFormValues, string>>>({});
  const [hotelErrors, setHotelErrors] = useState<Partial<Record<keyof TravelerFormValues, string>>>({});
  const [shinkansenErrors, setShinkansenErrors] = useState<Partial<Record<keyof ShinkansenFormValues, string>>>(
    {}
  );
  const showRailSection = transportTypes.hasRail === true && shinkansenInfo !== null;
  const showTravelerSection = travelerFields !== null && formValues.traveler !== null;
  const showHotelSection = hotelFields !== null && hotelFormValues !== null;

  const validateTravelerFields = (): Partial<Record<keyof TravelerFormValues, string>> => {
    const newErrors: Partial<Record<keyof TravelerFormValues, string>> = {};
    if (travelerFields == null || formValues.traveler == null) return newErrors;

    const { required } = travelerFields;
    const traveler = formValues.traveler;

    if (required.includes('flight_first_name_kana') || required.includes('flight_last_name_kana')) {
      if (traveler.flightLastNameKana.trim() === '') {
        newErrors.flightLastNameKana = '姓（カナ）を入力してください';
      } else if (isKatakana(traveler.flightLastNameKana.trim()) === false) {
        newErrors.flightLastNameKana = 'カタカナで入力してください';
      }
      if (traveler.flightFirstNameKana.trim() === '') {
        newErrors.flightFirstNameKana = '名（カナ）を入力してください';
      } else if (isKatakana(traveler.flightFirstNameKana.trim()) === false) {
        newErrors.flightFirstNameKana = 'カタカナで入力してください';
      }
    }
    if (required.includes('flight_birthday') && traveler.flightBirthday === '') {
      newErrors.flightBirthday = '生年月日を入力してください';
    }
    if (required.includes('flight_gender') && traveler.flightGender === '') {
      newErrors.flightGender = '性別を選択してください';
    }
    if (required.includes('flight_tel') && traveler.flightTel.trim() === '') {
      newErrors.flightTel = '緊急連絡先を入力してください';
    }

    return newErrors;
  };

  const validateHotelFields = (): Partial<Record<keyof TravelerFormValues, string>> => {
    const newErrors: Partial<Record<keyof TravelerFormValues, string>> = {};
    if (hotelFields == null || hotelFormValues == null) return newErrors;

    const { required } = hotelFields;

    if (required.includes('flight_first_name_kana') || required.includes('flight_last_name_kana')) {
      if (hotelFormValues.flightLastNameKana.trim() === '') {
        newErrors.flightLastNameKana = '姓（カナ）を入力してください';
      } else if (isKatakana(hotelFormValues.flightLastNameKana.trim()) === false) {
        newErrors.flightLastNameKana = 'カタカナで入力してください';
      }
      if (hotelFormValues.flightFirstNameKana.trim() === '') {
        newErrors.flightFirstNameKana = '名（カナ）を入力してください';
      } else if (isKatakana(hotelFormValues.flightFirstNameKana.trim()) === false) {
        newErrors.flightFirstNameKana = 'カタカナで入力してください';
      }
    }
    if (required.includes('flight_tel') && hotelFormValues.flightTel.trim() === '') {
      newErrors.flightTel = '連絡先電話番号を入力してください';
    }

    return newErrors;
  };

  const buildShinkansenSeatValue = (shinkansen: ShinkansenFormValues): string => {
    if (shinkansen.shinkansenSeat === 'その他' && shinkansen.shinkansenSeatDetail.trim() !== '') {
      return `${shinkansen.shinkansenSeat}:${shinkansen.shinkansenSeatDetail.trim()}`;
    }
    return shinkansen.shinkansenSeat;
  };

  const validateShinkansenFields = (): Partial<Record<keyof ShinkansenFormValues, string>> => {
    const newErrors: Partial<Record<keyof ShinkansenFormValues, string>> = {};
    if (shinkansenInfo == null || formValues.shinkansen == null) return newErrors;

    const { settings } = shinkansenInfo;
    const shinkansen = formValues.shinkansen;

    const requireAddress = settings.useQr === false && shinkansen.shinkansenTicketType !== 'ex';

    if (requireAddress === true) {
      if (
        shinkansen.shinkansenAddressType === SHINKANSEN_ADDRESS_TYPES.text &&
        settings.enabledCustomizeAddress === true
      ) {
        if (shinkansen.postcode.trim() === '') {
          newErrors.postcode = '郵便番号を入力してください';
        }
        if (shinkansen.shinkansenAddress.trim() === '') {
          newErrors.shinkansenAddress = '切符送り先住所を入力してください';
        }
        if (shinkansen.shinkansenAddressee.trim() === '') {
          newErrors.shinkansenAddressee = '切符送り先宛名を入力してください';
        }
      }
      if (
        shinkansen.shinkansenAddressType === SHINKANSEN_ADDRESS_TYPES.select &&
        settings.hasDepartments === true
      ) {
        if (shinkansen.departmentId == null) {
          newErrors.departmentId = '部署を選択してください';
        }
      }
    }

    if (shinkansen.shinkansenTicketType === 'ex' && settings.exicUsageMode === 'card_based') {
      if (shinkansen.exicCardId == null) {
        newErrors.exicCardId = 'EXIC会員IDを選択してください';
      }
    }

    return newErrors;
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormValues, string>> = {};

    if (
      settingFlags.showDestinationBusinessTrip &&
      (settingFlags.isDestinationBusinessTripRequired || approvalRequired === true)
    ) {
      if (formValues.finalDestination.trim() === '') {
        newErrors.finalDestination = '出張先を入力してください';
      }
    }

    if (
      settingFlags.showPurposeBusinessTrip &&
      (settingFlags.isPurposeBusinessTripRequired || approvalRequired === true)
    ) {
      if (formValues.purpose.trim() === '') {
        newErrors.purpose = '出張の目的を入力してください';
      }
    }

    if (settingFlags.showInternalNumber && settingFlags.isInternalNumberRequired) {
      if (formValues.internalNumber.trim() === '') {
        newErrors.internalNumber = '社内管理番号を入力してください';
      }
    }

    if (settingFlags.showChargingDepartment && settingFlags.isChargingDepartmentRequired) {
      if (formValues.chargingDepartmentId == null) {
        newErrors.chargingDepartmentId = '費用負担部署を選択してください';
      }
    }

    if (settingFlags.showProjectName && settingFlags.isProjectNameRequired) {
      if (formValues.projectId == null) {
        newErrors.projectId = 'プロジェクトを選択してください';
      }
    }

    const newTravelerErrors = validateTravelerFields();
    const newHotelErrors = validateHotelFields();
    const newShinkansenErrors = validateShinkansenFields();

    setErrors(newErrors);
    setTravelerErrors(newTravelerErrors);
    setHotelErrors(newHotelErrors);
    setShinkansenErrors(newShinkansenErrors);
    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(newTravelerErrors).length === 0 &&
      Object.keys(newHotelErrors).length === 0 &&
      Object.keys(newShinkansenErrors).length === 0
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const shinkansen = formValues.shinkansen;
      const resolvedDepartment =
        shinkansen != null && shinkansen.shinkansenAddressType === SHINKANSEN_ADDRESS_TYPES.select
          ? options.departments.find(department => department.id === shinkansen.departmentId)
          : null;
      const resolvedBase =
        shinkansen != null && shinkansen.shinkansenAddressType === SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect
          ? options.organizationBases.find(base => base.id === shinkansen.organizationBaseId)
          : null;
      const resolvedShinkansen =
        shinkansen != null
          ? {
              ...shinkansen,
              shinkansenAddress:
                shinkansen.shinkansenAddress || resolvedDepartment?.address || resolvedBase?.address || '',
              shinkansenAddressee:
                shinkansen.shinkansenAddressee || resolvedDepartment?.addressee || resolvedBase?.addressee || '',
              postcode: shinkansen.postcode || resolvedDepartment?.postalCode || resolvedBase?.postalCode || '',
              organizationAddressId:
                shinkansen.shinkansenAddressType === SHINKANSEN_ADDRESS_TYPES.select
                  ? resolvedDepartment?.organizationAddressId || null
                  : null
            }
          : null;
      const emptyTraveler: TravelerFormValues = {
        flightFirstName: '',
        flightLastName: '',
        flightFirstNameKana: '',
        flightLastNameKana: '',
        flightFirstNameRoman: '',
        flightLastNameRoman: '',
        flightBirthday: '',
        flightGender: '',
        flightTel: '',
        airSeat: ''
      };
      const baseTraveler: TravelerFormValues = {
        ...emptyTraveler,
        ...(hotelFormValues || {}),
        ...(formValues.traveler || {})
      };
      const updatedTraveler =
        resolvedShinkansen != null
          ? {
              ...baseTraveler,
              shinkansenTicketType: resolvedShinkansen.shinkansenTicketType,
              shinkansenSeatType: resolvedShinkansen.shinkansenSeatType,
              shinkansenSeat: buildShinkansenSeatValue(resolvedShinkansen),
              exicNumber: resolvedShinkansen.exicNumber,
              exicPassword: resolvedShinkansen.exicPassword,
              exicCardId: resolvedShinkansen.exicCardId
            }
          : baseTraveler;
      onSubmit({ ...formValues, shinkansen: resolvedShinkansen, traveler: updatedTraveler });
    }
  };

  const handleTextChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFormValues(prev => ({ ...prev, [field]: newValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value ? Number(e.target.value) : null;
    setFormValues(prev => ({ ...prev, [field]: newValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCommonTextChange = (field: keyof CommonFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    handleTextChange(field)(e);
  };

  const handleCommonSelectChange =
    (field: 'chargingDepartmentId' | 'projectId') => (e: React.ChangeEvent<HTMLSelectElement>) => {
      handleSelectChange(field)(e);
    };

  const handleTravelerTextChange =
    (field: keyof TravelerFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setFormValues(prev => ({
        ...prev,
        traveler: prev.traveler ? { ...prev.traveler, [field]: newValue } : null
      }));
      if (travelerErrors[field]) {
        setTravelerErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const handleTravelerSelectChange =
    (field: keyof TravelerFormValues) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value;
      setFormValues(prev => ({
        ...prev,
        traveler: prev.traveler ? { ...prev.traveler, [field]: newValue } : null
      }));
      if (travelerErrors[field]) {
        setTravelerErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const handleHotelTextChange = (field: keyof TravelerFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setHotelFormValues(prev => (prev ? { ...prev, [field]: newValue } : null));
    if (hotelErrors[field]) {
      setHotelErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleShinkansenTextChange =
    (field: keyof ShinkansenFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setFormValues(prev => ({
        ...prev,
        shinkansen: prev.shinkansen ? { ...prev.shinkansen, [field]: newValue } : null
      }));
      if (shinkansenErrors[field]) {
        setShinkansenErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const handleShinkansenSelectChange =
    (field: keyof ShinkansenFormValues) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value;
      setFormValues(prev => ({
        ...prev,
        shinkansen: prev.shinkansen ? { ...prev.shinkansen, [field]: newValue } : null
      }));
      if (shinkansenErrors[field]) {
        setShinkansenErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const handleShinkansenAddressTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value as ShinkansenFormValues['shinkansenAddressType'];
    const department =
      newValue === SHINKANSEN_ADDRESS_TYPES.select
        ? options.departments.find(dept => dept.id === formValues.shinkansen?.departmentId)
        : null;
    const nextAddress =
      newValue === SHINKANSEN_ADDRESS_TYPES.select
        ? department?.address || ''
        : newValue === SHINKANSEN_ADDRESS_TYPES.text
        ? formValues.shinkansen?.shinkansenAddress || ''
        : '';
    const nextAddressee =
      newValue === SHINKANSEN_ADDRESS_TYPES.select
        ? department?.addressee || ''
        : newValue === SHINKANSEN_ADDRESS_TYPES.text
        ? formValues.shinkansen?.shinkansenAddressee || ''
        : '';
    const nextPostcode =
      newValue === SHINKANSEN_ADDRESS_TYPES.select
        ? department?.postalCode || ''
        : newValue === SHINKANSEN_ADDRESS_TYPES.text
        ? formValues.shinkansen?.postcode || ''
        : '';
    setFormValues(prev => ({
      ...prev,
      shinkansen: prev.shinkansen
        ? {
            ...prev.shinkansen,
            shinkansenAddressType: newValue,
            shinkansenAddress: nextAddress,
            shinkansenAddressee: nextAddressee,
            postcode: nextPostcode,
            organizationAddressId:
              newValue === SHINKANSEN_ADDRESS_TYPES.select ? department?.organizationAddressId || null : null,
            organizationBaseId: null
          }
        : null
    }));
  };

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const departmentId = e.target.value ? Number(e.target.value) : null;
    const department = options.departments.find(d => d.id === departmentId);
    setFormValues(prev => ({
      ...prev,
      shinkansen: prev.shinkansen
        ? {
            ...prev.shinkansen,
            departmentId,
            shinkansenAddress: department?.address || '',
            shinkansenAddressee: department?.addressee || '',
            postcode: department?.postalCode || '',
            organizationAddressId: department?.organizationAddressId || null
          }
        : null
    }));
  };

  const handleOrganizationBaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const baseId = e.target.value ? Number(e.target.value) : null;
    const base = options.organizationBases.find(b => b.id === baseId);
    setFormValues(prev => ({
      ...prev,
      shinkansen: prev.shinkansen
        ? {
            ...prev.shinkansen,
            organizationBaseId: baseId,
            shinkansenAddress: base?.address || '',
            shinkansenAddressee: base?.addressee || '',
            postcode: base?.postalCode || '',
            organizationAddressId: null
          }
        : null
    }));
  };

  const hasVisibleFields =
    settingFlags.showDestinationBusinessTrip ||
    settingFlags.showPurposeBusinessTrip ||
    settingFlags.showInternalNumber ||
    settingFlags.showChargingDepartment ||
    settingFlags.showProjectName ||
    travelerFields !== null ||
    hotelFields !== null ||
    shinkansenInfo !== null;

  if (hasVisibleFields === false) {
    return null;
  }

  const commonValues: CommonFormValues = {
    finalDestination: formValues.finalDestination,
    purpose: formValues.purpose,
    internalNumber: formValues.internalNumber,
    chargingDepartmentId: formValues.chargingDepartmentId,
    projectId: formValues.projectId
  };
  const commonErrors = errors as CommonErrors;
  const disabled = isSubmitting || editable === false;

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>出張申請に関する情報</h3>
      <form onSubmit={handleSubmit}>
        <ReservationInfoCommonSection
          values={commonValues}
          errors={commonErrors}
          settingFlags={settingFlags}
          approvalRequired={approvalRequired === true}
          departments={options.departments}
          projects={options.projects}
          styles={styles}
          onTextChange={handleCommonTextChange}
          onSelectChange={handleCommonSelectChange}
          disabled={disabled}
        />
        {showTravelerSection && travelerFields && formValues.traveler && (
          <ReservationInfoAirSection
            travelerFields={travelerFields}
            traveler={formValues.traveler}
            travelerErrors={travelerErrors}
            onTextChange={handleTravelerTextChange}
            onSelectChange={handleTravelerSelectChange}
            disabled={disabled}
            title="搭乗者情報"
            telLabel="緊急連絡先"
          />
        )}
        {showHotelSection && hotelFields && hotelFormValues && (
          <ReservationInfoAirSection
            travelerFields={hotelFields}
            traveler={hotelFormValues}
            travelerErrors={hotelErrors}
            onTextChange={handleHotelTextChange}
            onSelectChange={() => () => {}}
            disabled={disabled}
            title="宿泊者情報"
            telLabel="連絡先電話番号"
          />
        )}
        {showRailSection && formValues.shinkansen && shinkansenInfo && (
          <ReservationInfoShinkansenSection
            shinkansen={formValues.shinkansen}
            shinkansenErrors={shinkansenErrors}
            settings={shinkansenInfo.settings}
            departments={options.departments}
            organizationBases={options.organizationBases}
            exicCards={options.exicCards}
            styles={styles}
            onSelectChange={handleShinkansenSelectChange}
            onTextChange={handleShinkansenTextChange}
            onAddressTypeChange={handleShinkansenAddressTypeChange}
            onDepartmentChange={handleDepartmentChange}
            onOrganizationBaseChange={handleOrganizationBaseChange}
            disabled={disabled}
          />
        )}
        <button
          type="submit"
          style={{
            ...styles.submitButton,
            ...(editable === false ? styles.submitButtonDisabled : {})
          }}
          disabled={disabled}
        >
          {editable === false ? '送信済み' : isSubmitting ? '送信中...' : '送信'}
        </button>
      </form>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '20px',
    maxWidth: '300px'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '16px',
    marginTop: 0
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '6px',
    color: '#333'
  },
  required: {
    color: '#e74c3c',
    marginLeft: '4px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  inputError: {
    borderColor: '#e74c3c'
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  },
  submitButton: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '8px'
  },
  submitButtonDisabled: {
    backgroundColor: '#6c757d',
    cursor: 'not-allowed'
  },
  section: {
    marginTop: '16px',
    paddingTop: '8px',
    borderTop: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 'bold',
    marginBottom: '12px'
  }
};

export default ReservationInfoForm;
